import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import DateRangePicker from "../../common/DateRangePicker";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import {
  BDC_INVOICE_DEFAULT_COPIES,
  exportBdcInvoices,
  getBdcInvoicePdf,
  listBdcInvoices,
} from "../../../services";
import { featureAccess } from "../../../permissions/access";
import { BdcInvoiceListParams, BdcInvoiceStatus, BdcInvoiceSummary } from "../../../types/bdc.type";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import BranchChip from "../Branch/BranchChip";
import BranchSelector from "../Branch/BranchSelector";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { bdcNewCreditNoteLink, bdcShipmentLink } from "../constants";
import { baht, pcs, thaiDate, todayBkk } from "../format";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../styles";
import { useBdcDcs } from "../useBdcDcs";
import { useBlobExport } from "../useBlobExport";
import CancelInvoiceDialog from "./CancelInvoiceDialog";
import InvoiceCopiesField from "./InvoiceCopiesField";
import InvoiceStatusChip from "./InvoiceStatusChip";
import { openPdfResponse } from "./pdf";

const ALL = "all";
const PAGE_SIZES = [50, 100];

/** The first day of the Bangkok month of `date` (`YYYY-MM-DD`). */
export const monthStart = (date: string): string => `${date.slice(0, 7)}-01`;

/** The list query of the register: blank filters are left out; the range is in INV dates. */
export const registerParams = (filters: {
  dateFrom: string;
  dateTo: string;
  status: string;
  dcCode: string;
  search: string;
  isTest: boolean;
  companyId?: string;
}): Omit<BdcInvoiceListParams, "page" | "limit"> => ({
  date_from: filters.dateFrom || undefined,
  date_to: filters.dateTo || undefined,
  ...(filters.status !== ALL ? { status: filters.status as BdcInvoiceStatus } : {}),
  ...(filters.dcCode !== ALL ? { dc_code: filters.dcCode } : {}),
  ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
  ...(filters.isTest ? { is_test: true } : {}),
  ...(filters.companyId ? { company_id: filters.companyId } : {}),
});

/** เล่ม / เล่มที่ of an INV (`VY 55675`), or "—" before the book numbers exist. */
export const bookLabel = (row: Pick<BdcInvoiceSummary, "book_code" | "book_no">): string =>
  row.book_code || row.book_no != null ? `${row.book_code ?? ""} ${row.book_no ?? ""}`.trim() : "—";

/**
 * BDC · ทะเบียน INV (W13) — every real INV in a range of INV dates for accounting (output VAT),
 * cancelled ones included with their reason; the test series (TB…) only behind its switch and
 * never in the Excel. PDF per INV; ยกเลิก INV for the desk; P2: CN แล้ว / สุทธิ / เล่ม and
 * สร้าง CN from an issued INV.
 */
export default function BdcInvoiceRegister() {
  const role = useUserRole();
  const canCancel = featureAccess.bdcDesk(role);
  const canCreditNote = featureAccess.bdcCreditNoteWrite(role);
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);
  const { dcs } = useBdcDcs();
  const { busy, run } = useBlobExport();

  const today = todayBkk();
  const [dateFrom, setDateFrom] = useState(monthStart(today));
  const [dateTo, setDateTo] = useState(today);
  const [status, setStatus] = useState<string>(ALL);
  const [dcCode, setDcCode] = useState<string>(ALL);
  const [searchText, setSearchText] = useState("");
  const search = useDebounce(searchText, 400);
  const [isTest, setIsTest] = useState(false);
  // the page belongs to one filter set: any filter change reads as page 1
  const [pageState, setPageState] = useState<{ key: string; page: number }>({ key: "", page: 0 });
  const [limit, setLimit] = useState(PAGE_SIZES[0]);

  const [rows, setRows] = useState<BdcInvoiceSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  const [copies, setCopies] = useState(BDC_INVOICE_DEFAULT_COPIES);
  const [cancelling, setCancelling] = useState<BdcInvoiceSummary | null>(null);

  const params = useMemo(
    () => registerParams({ dateFrom, dateTo, status, dcCode, search, isTest, companyId: branch.companyId }),
    [dateFrom, dateTo, status, dcCode, search, isTest, branch.companyId],
  );
  const paramsKey = JSON.stringify(params);
  const page = pageState.key === paramsKey ? pageState.page : 0;
  const setPage = (next: number) => setPageState({ key: paramsKey, page: next });

  const load = useCallback(async () => {
    if (!branchesReady) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcInvoices({ ...params, page: page + 1, limit });
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [params, page, limit, branchesReady]);

  useEffect(() => {
    load();
  }, [load]);

  const pickRange = (from: Dayjs, to: Dayjs) => {
    setDateFrom(from.format("YYYY-MM-DD"));
    setDateTo(to.format("YYYY-MM-DD"));
  };

  const openPdf = async (invoiceNo: string) => {
    setPdfBusy(invoiceNo);
    try {
      openPdfResponse(await getBdcInvoicePdf(invoiceNo, copies), `${invoiceNo}.pdf`);
    } catch {
      // reported by the interceptor
    } finally {
      setPdfBusy(null);
    }
  };

  const issued = rows.filter((r) => r.status === "issued");
  const dcOptions = branch.companyId ? dcs.filter((dc) => dc.company_id === branch.companyId) : dcs;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            BDC · ทะเบียน INV
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ใช้ทำรายงานภาษีขาย · ใบยกเลิกยังแสดง (มีเหตุผล) · {isTest ? "กำลังดูชุดทดสอบ TB… (ไม่อยู่ในไฟล์ Excel)" : "ไม่รวมใบทดสอบ TB…"}
          </Typography>
        </Box>
        <Tooltip title={isTest ? "Excel ทะเบียนมีเฉพาะ INV จริง — ปิดสวิตช์ชุดทดสอบก่อน" : ""}>
          <span>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              disabled={isTest}
              loading={busy === "register"}
              onClick={() =>
                run("register", () => exportBdcInvoices(params), `bdc-invoice-register-${dateFrom}-${dateTo}.xlsx`)
              }
            >
              Export Excel
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
            <BranchSelector branches={branches} value={branch.selected} onChange={branch.select} />
            <DateRangePicker dateFrom={dayjs(dateFrom)} dateTo={dayjs(dateTo)} onChange={pickRange} />
            <TextField select size="small" label="สถานะ" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 140 }}>
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              <MenuItem value="issued">ออกแล้ว</MenuItem>
              <MenuItem value="cancelled">ยกเลิก</MenuItem>
            </TextField>
            <TextField select size="small" label="DC" value={dcCode} onChange={(e) => setDcCode(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value={ALL}>ทุก DC</MenuItem>
              {dcOptions.map((dc) => (
                <MenuItem key={dc.dc_code} value={dc.dc_code}>
                  {dc.dc_code} {dc.dc_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="เลข INV / เลข PO"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: 200 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControlLabel
              control={<Switch checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
              label="ชุดทดสอบ (TB…)"
            />
            <InvoiceCopiesField value={copies} onChange={setCopies} />
          </Stack>
        </Card>

        {loading && <LinearProgress />}

        <Card sx={CARD_SX}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #E3E5EA" }}>
            <Typography className="sub">
              ทั้งหมด {pcs(count)} ใบ · หน้านี้ ออกแล้ว {pcs(issued.length)} ใบ รวม ฿{baht(issued.reduce((s, r) => s + r.grand_total, 0))}
            </Typography>
          </Box>
          <Box sx={SCROLL_SX}>
            <table className="bdc-t" data-testid="bdc-invoice-register">
              <thead>
                <tr>
                  <th>เลข INV</th>
                  <th>วันที่</th>
                  <th>ครบกำหนด</th>
                  <th>สาขา</th>
                  <th>PO</th>
                  <th>DC</th>
                  <th>ผู้ซื้อ</th>
                  <th className="n">มูลค่า</th>
                  <th className="n">VAT</th>
                  <th className="n">รวม</th>
                  <th className="n">CN แล้ว</th>
                  <th className="n">สุทธิ</th>
                  <th>เล่ม</th>
                  <th>สถานะ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.invoice_uuid}>
                    <td className="mono">
                      <b>{row.invoice_no}</b>
                    </td>
                    <td>{thaiDate(row.invoice_date)}</td>
                    <td>{thaiDate(row.due_date)}</td>
                    <td>
                      <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />
                    </td>
                    <td className="mono">
                      <RouterLink to={bdcShipmentLink(row.po_number, "inv")}>{row.po_number}</RouterLink>
                    </td>
                    <td>
                      <span className="dc">{row.dc_code}</span> <span className="sub">{row.dc_name ?? ""}</span>
                    </td>
                    <td>
                      {row.buyer_name}
                      <span className="sub">
                        {row.buyer_branch ? ` (${row.buyer_branch})` : ""} {row.buyer_tax_id ?? ""}
                      </span>
                    </td>
                    <td className="n">{baht(row.subtotal)}</td>
                    <td className="n">{baht(row.vat_amount)}</td>
                    <td className="n">
                      <b>{baht(row.grand_total)}</b>
                    </td>
                    <td className="n">{row.credited_amount ? baht(row.credited_amount) : "—"}</td>
                    <td className="n">{baht(row.net_amount ?? row.grand_total - (row.credited_amount ?? 0))}</td>
                    <td className="mono">{bookLabel(row)}</td>
                    <td>
                      <InvoiceStatusChip invoice={row} />
                      {row.status === "cancelled" && row.cancel_reason && <div className="sub">{row.cancel_reason}</div>}
                    </td>
                    <td>
                      <Stack direction="row" gap={1}>
                        <Button size="small" variant="outlined" loading={pdfBusy === row.invoice_no} onClick={() => openPdf(row.invoice_no)}>
                          PDF
                        </Button>
                        {canCreditNote && row.status === "issued" && (
                          <Button size="small" variant="outlined" component={RouterLink} to={bdcNewCreditNoteLink(row.invoice_no)}>
                            สร้าง CN
                          </Button>
                        )}
                        {canCancel && row.status === "issued" && (
                          <Button size="small" variant="outlined" color="error" onClick={() => setCancelling(row)}>
                            ยกเลิก
                          </Button>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={15} className="sub" style={{ textAlign: "center", padding: 24 }}>
                      ไม่มี INV ในช่วงนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          <TablePagination
            component="div"
            count={count}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
            labelRowsPerPage="ต่อหน้า"
          />
        </Card>
      </Stack>

      <CancelInvoiceDialog
        invoiceNo={cancelling?.invoice_no ?? null}
        poNumber={cancelling?.po_number}
        onClose={() => setCancelling(null)}
        onCancelled={() => {
          setCancelling(null);
          load();
        }}
      />
    </Box>
  );
}
